/**
 * Configuration error classes
 */

/**
 * Base configuration error
 */
export class ConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConfigError';
  }
}

/**
 * Error when environment variables are missing or invalid
 */
export class ConfigValidationError extends ConfigError {
  public readonly errors: string[];

  constructor(errors: string[]) {
    const message = `Configuration validation failed:\n${errors.map(e => `  • ${e}`).join('\n')}`;
    super(message);
    this.name = 'ConfigValidationError';
    this.errors = errors;
  }
}

/**
 * Error when selected environment doesn't exist or is misconfigured
 */
export class EnvironmentNotFoundError extends ConfigError {
  constructor(environmentName: string, availableEnvironments: string[]) {
    super(
      `Environment "${environmentName}" not found. ` +
      `Available environments: ${availableEnvironments.join(', ')}`
    );
    this.name = 'EnvironmentNotFoundError';
  }
}

/**
 * Error when environment mismatch detected between frontend and API
 */
export class EnvironmentMismatchError extends ConfigError {
  constructor(frontendEnv: string, apiEnv: string) {
    super(
      `Environment mismatch detected!\n` +
      `  Frontend: ${frontendEnv}\n` +
      `  API: ${apiEnv}\n\n` +
      `This usually means the API URL in your .env file is incorrect.\n` +
      `Check that your ${frontendEnv.toUpperCase()}_API_URL matches your ${frontendEnv} API endpoint.`
    );
    this.name = 'EnvironmentMismatchError';
  }
}

/**
 * Error when authentication provider fails to initialize
 */
export class AuthProviderError extends ConfigError {
  constructor(message: string, cause?: Error) {
    super(`Authentication provider initialization failed: ${message}`);
    this.name = 'AuthProviderError';
    if (cause) {
      this.cause = cause;
    }
  }
}
